/**
 * areaMarker — the real hit area of an area ability, drawn as a ring at
 * exactly its gameplay radius (plan 29 F6, lane BS 2026-10-11).
 *
 * This is the totem aura-ring contract (`vfx:summon_placed` → SummonVFXHandler
 * draws fill + stroke at `radius`) used for every AOE: Divine Storm drew no
 * ring at all, and the zones that did draw drew their own guess. The ring is
 * placed `exact` (no breathing past the radius) in the ability's colour and
 * expires when the area's window ends.
 */
import { EventBus } from '../../../shared/EventBus';
import { getAbilityData } from '../../../shared/content/AbilityDataAdapter';
import { ABILITY_SCHOOL_COLORS } from '../../../shared/constants/AbilitySchoolColors';

/** Shortest time a ring stays up, so an instant area still reads (ms). */
export const AREA_MARKER_MIN_MS = 350;

/** `#RRGGBB` → number, else undefined. */
function hex(value: unknown): number | undefined {
  return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value) ? parseInt(value.slice(1), 16) : undefined;
}

/** The ability's area colour: TOML aura colour, else its school colour. */
export function abilityAreaColor(abilityId: string): number | undefined {
  const data = getAbilityData(abilityId) as { school?: string; vfx?: { auraColor?: string } } | undefined;
  return hex(data?.vfx?.auraColor) ?? (data?.school ? ABILITY_SCHOOL_COLORS[data.school] : undefined);
}

let seq = 0;

/** Place the ring for one area cast; it expires after `windowMs`. */
export function placeAreaMarker(abilityId: string, casterId: string, x: number, y: number, radius: number, windowMs: number): string {
  const instanceId = `area_${abilityId}_${casterId}_${++seq}`;
  const bus = EventBus.getInstance();
  bus.emit('vfx:summon_placed', {
    type: 'zone', x, y, radius, instanceId, color: abilityAreaColor(abilityId), exact: true,
  });
  setTimeout(() => bus.emit('vfx:summon_expired', { instanceId }), Math.max(AREA_MARKER_MIN_MS, windowMs));
  return instanceId;
}
